// Implements: F-CNV-003, F-CNV-007, F-UIX-009, F-UIX-007, F-UIX-017
// Implements: F-PRV-003
/**
 * What the person sees of the turn in flight. Pure presentation.
 *
 * Every phase has its own sentence and none is inferred from another: `cancelling`
 * ("we asked") and `cancelled` ("it stopped") are different screens, because showing
 * the second on the strength of the first would say a paid run is over when it may
 * still be running. The version a successful turn wrote is linked by the thread itself,
 * from the update message the server writes, so it is not linked twice.
 *
 * While the run is `running` and its stream has said where it is (F-PRV-003), the sentence
 * gives way to the run's stages: one per step of its flow, by role, and a last one for the
 * review and delivery, each done, current (with the busy ring) or pending, and "paso X de Y"
 * when the flow is known. Nothing is invented: no percentage, no stage the server did not
 * name; a flow or a role this client does not know reads as the generic stage. The current
 * stage is announced through ONE polite live region whose text changes, so each new stage
 * is heard once (the region is inserted empty and named right after, so the first stage is a
 * change too); the reassurance message that rotates every few seconds is not live, so it
 * never talks over the person. The ring stands still for whoever asked for reduced motion.
 *
 * The cancel control is shown while the turn is active and enabled only when the
 * server has named the run and no cancel is already up.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Spinner } from "../../../shared/ui/atoms/Spinner";
import { keyShortcutLabel } from "../../../shared/ui/useKeyShortcut";
import type { KeyShortcut } from "../../../shared/ui/useKeyShortcut";
import { isActivePhase } from "../../../types/generation";
import type { GenerationPhase, RunProgress } from "../../../types/generation";
import styles from "./TurnProgress.module.css";

/** The status region: where the focus goes once a retry or an edit is accepted. */
export const STATUS_REGION_ID = "conversation-turn-status";

/** Calls `tick` every `ms` until the returned function is called (checklist 16). */
export interface Ticker {
  readonly every: (ms: number, tick: () => void) => () => void;
}

const BROWSER_TICKER: Ticker = {
  every: (ms, tick) => {
    const id = setInterval(tick, ms);
    return () => {
      clearInterval(id);
    };
  },
};

/** How long each reassurance message stays, in milliseconds. */
export const REASSURANCE_MS = 6000;

type ReassuranceKey =
  | "conversation.progress.reassure.first"
  | "conversation.progress.reassure.second"
  | "conversation.progress.reassure.third"
  | "conversation.progress.reassure.fourth";

type StageKey =
  | "conversation.progress.stage.normalize"
  | "conversation.progress.stage.desktop"
  | "conversation.progress.stage.mobile"
  | "conversation.progress.stage.diagram"
  | "conversation.progress.stage.assess"
  | "conversation.progress.stage.summarize"
  | "conversation.progress.stage.working"
  | "conversation.progress.stage.finishing";

const REASSURANCE: readonly ReassuranceKey[] = [
  "conversation.progress.reassure.first",
  "conversation.progress.reassure.second",
  "conversation.progress.reassure.third",
  "conversation.progress.reassure.fourth",
];

/** The stage the server names after the last step, and the one for a flow it cannot read. */
const FINISHING = "finishing";
const WORKING = "working";

/** The label of each stage this client knows; any other reads as the generic one. */
const STAGE_LABELS: ReadonlyMap<string, StageKey> = new Map<string, StageKey>([
  ["normalize", "conversation.progress.stage.normalize"],
  ["desktop", "conversation.progress.stage.desktop"],
  ["mobile", "conversation.progress.stage.mobile"],
  ["diagram", "conversation.progress.stage.diagram"],
  ["assess", "conversation.progress.stage.assess"],
  ["summarize", "conversation.progress.stage.summarize"],
  [WORKING, "conversation.progress.stage.working"],
  [FINISHING, "conversation.progress.stage.finishing"],
]);

type StageState = "done" | "current" | "pending";

const STATE_LABELS: Readonly<
  Record<
    StageState,
    | "conversation.progress.state.done"
    | "conversation.progress.state.current"
    | "conversation.progress.state.pending"
  >
> = {
  done: "conversation.progress.state.done",
  current: "conversation.progress.state.current",
  pending: "conversation.progress.state.pending",
};

function labelOf(stage: string): StageKey {
  return STAGE_LABELS.get(stage) ?? "conversation.progress.stage.working";
}

interface StageList {
  readonly roles: readonly string[];
  readonly current: number;
}

/** The stages to list (the flow's steps, then the review), and the index of the current one. */
function stagesOf(progress: RunProgress): StageList {
  const known = progress.stepRoles.length > 0;
  const steps = known ? progress.stepRoles : [WORKING];
  const roles = [...steps, FINISHING];
  if (progress.stage === FINISHING) return { roles, current: roles.length - 1 };
  if (!known) return { roles, current: 0 };
  return { roles, current: Math.min(progress.stepsDone, steps.length) };
}

/** The index of the reassurance message on show; moves on every `REASSURANCE_MS`. */
function useRotation(ticker: Ticker): number {
  const [index, setIndex] = useState(0);
  useEffect(
    () =>
      ticker.every(REASSURANCE_MS, () => {
        setIndex((shown) => (shown + 1) % REASSURANCE.length);
      }),
    [ticker],
  );
  return index;
}

interface StagesProps {
  readonly progress: RunProgress;
  readonly ticker: Ticker;
}

function Stages({ progress, ticker }: StagesProps): ReactElement {
  const { t } = useTranslation();
  const rotation = useRotation(ticker);
  const { roles, current } = stagesOf(progress);
  const currentLabel = t(labelOf(roles[current] ?? WORKING));
  const total = progress.stepsTotal;
  const announcement = t("conversation.progress.now", { stage: currentLabel });
  // The live region is inserted EMPTY and named right after (POLISH-8 B3): a region inserted
  // with its text already in it is not announced by most screen readers, only a change is.
  // React renders it childless; the effect writes the text once the node is in the document.
  const live = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (live.current !== null) live.current.textContent = announcement;
  }, [announcement]);

  return (
    <div className={styles.stages}>
      {/* The phase sentence stays as the heading; the live region below is what is announced. */}
      <p className={styles.result}>{t("conversation.status.running")}</p>
      <p ref={live} className={styles.visuallyHidden} role="status" aria-live="polite" />
      <ol className={styles.list} aria-label={t("conversation.progress.listLabel")}>
        {roles.map((role, index) => {
          const state: StageState =
            index < current ? "done" : index === current ? "current" : "pending";
          return (
            <li
              key={`${String(index)}-${role}`}
              className={styles.stage}
              data-state={state}
              aria-current={state === "current" ? "step" : undefined}
            >
              <span className={styles.marker} aria-hidden="true" />
              {t(labelOf(role))}
              <span className={styles.visuallyHidden}>
                {" "}
                {t(STATE_LABELS[state])}
              </span>
            </li>
          );
        })}
      </ol>
      {total !== null && total > 0 ? (
        <p className={styles.step}>
          {t("conversation.progress.step", {
            current: Math.min(progress.stepsDone + 1, total),
            total,
          })}
        </p>
      ) : null}
      <p className={styles.reassurance}>
        {t(REASSURANCE[rotation] ?? "conversation.progress.reassure.first")}
      </p>
    </div>
  );
}

/** Escape cancels the turn in flight (F-UIX-017); the container binds it to the conversation. */
export const CANCEL_SHORTCUT: KeyShortcut = { key: "Escape" };

/** Whether the turn can be cancelled now: the rule of the button, its hint and its shortcut. */
export function isCancellable(phase: GenerationPhase, canCancel: boolean): boolean {
  return canCancel && phase !== "cancelling" && isActivePhase(phase);
}

export interface TurnProgressProps {
  readonly phase: Exclude<GenerationPhase, "idle">;
  /** The server has named the run, so there is something to cancel. */
  readonly canCancel: boolean;
  readonly onCancel: () => void;
  /** What the run's stream last said about its stages (F-PRV-003); `null` before it said anything. */
  readonly progress?: RunProgress | null;
  /** Moves the reassurance message on; tests inject one that ticks by hand (checklist 16). */
  readonly ticker?: Ticker;
}

export function TurnProgress({
  phase,
  canCancel,
  onCancel,
  progress = null,
  ticker = BROWSER_TICKER,
}: TurnProgressProps): ReactElement {
  const { t } = useTranslation();
  const active = isActivePhase(phase);
  const staged = phase === "running" && progress !== null;
  // The same rule as the button and as the Escape shortcut (`useCancelShortcut`): one cancel.
  const cancellable = isCancellable(phase, canCancel);
  // The hint in words describes the button, so a screen reader hears the shortcut with it.
  const cancelHintId = useId();

  return (
    <div id={STATUS_REGION_ID} className={styles.progress} tabIndex={-1}>
      {staged ? (
        <Stages progress={progress} ticker={ticker} />
      ) : active ? (
        <Spinner label={t(`conversation.status.${phase}`)} />
      ) : (
        <p className={styles.result} role="status">
          {t(`conversation.status.${phase}`)}
        </p>
      )}
      {active ? (
        <Button
          variant="secondary"
          size="sm"
          disabled={!canCancel || phase === "cancelling"}
          keyShortcuts={cancellable ? keyShortcutLabel(CANCEL_SHORTCUT) : undefined}
          describedBy={cancellable ? cancelHintId : undefined}
          onClick={onCancel}
        >
          {t("conversation.cancel")}
        </Button>
      ) : null}
      {cancellable ? (
        <p id={cancelHintId} className={styles.reassurance}>
          {t("conversation.cancel.hint")}
        </p>
      ) : null}
    </div>
  );
}
